import i18n from '@/utils/i18n'
import { isUsdSiteCurrency } from '@/utils/siteBrand'

/** system/info payment_method: 0=계좌+테더, 1=테더만, 2=계좌만, 3=입출금 비활성, 4=PG */
export const PAYMENT_METHOD_BOTH = 0
export const PAYMENT_METHOD_TETHER_ONLY = 1
export const PAYMENT_METHOD_BANK_ONLY = 2
export const PAYMENT_METHOD_NONE = 3
export const PAYMENT_METHOD_PG = 4

export function resolvePaymentMethod (siteInfo) {
  const raw = siteInfo && siteInfo.payment_method != null
    ? Number(siteInfo.payment_method)
    : PAYMENT_METHOD_BOTH
  if (raw === PAYMENT_METHOD_TETHER_ONLY) return PAYMENT_METHOD_TETHER_ONLY
  if (raw === PAYMENT_METHOD_BANK_ONLY) return PAYMENT_METHOD_BANK_ONLY
  if (raw === PAYMENT_METHOD_NONE) return PAYMENT_METHOD_NONE
  if (raw === PAYMENT_METHOD_PG) {
    // PG는 currency=$ 전용. 잘못 설정된 사이트는 계좌+테더로 폴백
    if (!isUsdSiteCurrency(siteInfo)) return PAYMENT_METHOD_BOTH
    return PAYMENT_METHOD_PG
  }
  return PAYMENT_METHOD_BOTH
}

export function isPaymentMethodEnabled (paymentMethod) {
  return Number(paymentMethod) !== PAYMENT_METHOD_NONE
}

export function isSiteWalletEnabled (siteInfo) {
  return isPaymentMethodEnabled(resolvePaymentMethod(siteInfo))
}

export function isPgPaymentMethod (paymentMethodOrSiteInfo) {
  if (paymentMethodOrSiteInfo != null && typeof paymentMethodOrSiteInfo === 'object') {
    return resolvePaymentMethod(paymentMethodOrSiteInfo) === PAYMENT_METHOD_PG
  }
  return Number(paymentMethodOrSiteInfo) === PAYMENT_METHOD_PG
}

const WALLET_SIDE_MENU_KEYS = new Set(['deposit', 'withdraw'])
const ATTENDANCE_SIDE_MENU_KEY = 'attendance'

export function resolveSiteAttendanceMinAmount (siteInfo) {
  const raw = siteInfo && siteInfo.attendance != null
    ? Number(siteInfo.attendance)
    : 0
  return Number.isFinite(raw) ? raw : 0
}

export function isSiteAttendanceEnabled (siteInfo) {
  return resolveSiteAttendanceMinAmount(siteInfo) > 0
}

export function resolveSiteWithdrawLimitPercent (siteInfo) {
  const raw = siteInfo && siteInfo.withdraw_limit_percent != null
    ? Number(siteInfo.withdraw_limit_percent)
    : 0
  return Number.isFinite(raw) ? raw : 0
}

export function formatSiteWithdrawLimitPercent (siteInfo) {
  return `${resolveSiteWithdrawLimitPercent(siteInfo)}%`
}

export function filterSideMenuByWallet (items, walletEnabled) {
  if (!Array.isArray(items)) return []
  if (walletEnabled) return items
  return items.filter(item => !WALLET_SIDE_MENU_KEYS.has(item.key))
}

export function filterSideMenuByAttendance (items, attendanceEnabled) {
  if (!Array.isArray(items)) return []
  if (attendanceEnabled) return items
  return items.filter(item => item.key !== ATTENDANCE_SIDE_MENU_KEY)
}

export function paymentMethodAllowsBank (paymentMethod) {
  return paymentMethod === PAYMENT_METHOD_BOTH || paymentMethod === PAYMENT_METHOD_BANK_ONLY
}

export function paymentMethodAllowsTether (paymentMethod) {
  return paymentMethod === PAYMENT_METHOD_BOTH || paymentMethod === PAYMENT_METHOD_TETHER_ONLY
}

export function normalizeWalletTab (paymentMethod, currentTab) {
  if (paymentMethod === PAYMENT_METHOD_PG) return 'pg'
  const bank = paymentMethodAllowsBank(paymentMethod)
  const tether = paymentMethodAllowsTether(paymentMethod)
  if (currentTab === 'bank' && bank) return 'bank'
  if (currentTab === 'tether' && tether) return 'tether'
  if (bank) return 'bank'
  if (tether) return 'tether'
  return 'bank'
}

export function canSelectWalletTab (paymentMethod, tab) {
  if (paymentMethod === PAYMENT_METHOD_PG) return tab === 'pg'
  if (tab === 'bank') return paymentMethodAllowsBank(paymentMethod)
  if (tab === 'tether') return paymentMethodAllowsTether(paymentMethod)
  return false
}

export function getDepositSubtitle (paymentMethod) {
  if (paymentMethod === PAYMENT_METHOD_PG) {
    return i18n.t('payment.depositSubtitlePg')
  }
  if (paymentMethod === PAYMENT_METHOD_TETHER_ONLY) {
    return i18n.t('payment.depositSubtitleTether')
  }
  if (paymentMethod === PAYMENT_METHOD_BANK_ONLY) {
    return i18n.t('payment.depositSubtitleBank')
  }
  return i18n.t('payment.depositSubtitleBoth')
}

export function getWithdrawSubtitle (paymentMethod) {
  if (paymentMethod === PAYMENT_METHOD_PG) {
    return i18n.t('payment.withdrawSubtitlePg')
  }
  if (paymentMethod === PAYMENT_METHOD_TETHER_ONLY) {
    return i18n.t('payment.withdrawSubtitleTether')
  }
  if (paymentMethod === PAYMENT_METHOD_BANK_ONLY) {
    return i18n.t('payment.withdrawSubtitleBank')
  }
  return i18n.t('payment.withdrawSubtitleBoth')
}

/** 입금(pay)에서 선택 가능한 PG wayCode */
export const PG_DEPOSIT_WAY_OPTIONS = [
  { code: 'cashapp', labelKey: 'wallet.pgWayCashApp' },
  { code: 'chime', labelKey: 'wallet.pgWayChime' },
  { code: 'googlepay', labelKey: 'wallet.pgWayGooglePay' },
  { code: 'applepay', labelKey: 'wallet.pgWayApplePay' }
]

/** 출금(transfer)에서 선택 가능한 PG wayCode (CashApp / Chime만) */
export const PG_WITHDRAW_WAY_OPTIONS = [
  { code: 'cashapp', labelKey: 'wallet.pgWayCashApp' },
  { code: 'chime', labelKey: 'wallet.pgWayChime' }
]

/** @deprecated 하위 호환 — 입금은 PG_DEPOSIT_WAY_OPTIONS, 출금은 PG_WITHDRAW_WAY_OPTIONS */
export const PG_USER_WAY_OPTIONS = PG_DEPOSIT_WAY_OPTIONS

export function normalizePgDepositWayCode (wayCode) {
  const code = String(wayCode || '').trim().toLowerCase()
  if (code === 'chime') return 'chime'
  if (code === 'googlepay' || code === 'google_pay' || code === 'gpay') return 'googlepay'
  if (code === 'applepay' || code === 'apple_pay') return 'applepay'
  if (code === 'cashapp' || code === 'ecashapp') return 'cashapp'
  return ''
}

export function normalizePgWithdrawWayCode (wayCode) {
  const code = String(wayCode || '').trim().toLowerCase()
  if (code === 'chime') return 'chime'
  if (code === 'cashapp' || code === 'ecashapp') return 'cashapp'
  return ''
}

/** @deprecated 하위 호환 — 입금 정규화 */
export function normalizePgUserWayCode (wayCode) {
  return normalizePgDepositWayCode(wayCode)
}

/** UI 선택 wayCode → PG API wayCode (cashapp → ecashapp) */
export function toPgApiWayCode (wayCode) {
  const code = normalizePgDepositWayCode(wayCode)
  if (code === 'cashapp') return 'ecashapp'
  return code
}

/** 사이트 기본값 → 유저 선택 초기값 */
export function resolveDefaultPgUserWayCode (siteInfo, { forTransfer = false } = {}) {
  const raw = forTransfer
    ? (siteInfo && siteInfo.pg_transfer_way_code)
    : (siteInfo && siteInfo.pg_pay_way_code)
  if (forTransfer) {
    return normalizePgWithdrawWayCode(raw) || 'cashapp'
  }
  return normalizePgDepositWayCode(raw) || 'cashapp'
}

/** 출금 wayCode별 수취정보 필드 정의 */
export function getPgTransferReceiveFields (wayCode) {
  const code = String(wayCode || '').trim().toLowerCase()
  switch (code) {
    case 'cashapp':
    case 'ecashapp':
      return [
        { key: 'account', labelKey: 'wallet.pgCashtag', placeholderKey: 'wallet.pgCashtagPlaceholder', required: true }
      ]
    case 'paypal':
    case 'venmo':
    case 'epaypal':
      return [
        { key: 'account', labelKey: 'wallet.pgEmail', placeholderKey: 'wallet.pgEmailPlaceholder', required: true }
      ]
    case 'card':
      return [
        { key: 'account', labelKey: 'wallet.pgCardNumber', placeholderKey: 'wallet.pgCardNumberPlaceholder', required: true },
        { key: 'master', labelKey: 'wallet.pgCardValid', placeholderKey: 'wallet.pgCardValidPlaceholder', required: true }
      ]
    case 'ach':
      return [
        { key: 'account', labelKey: 'wallet.pgAccountNumber', placeholderKey: 'wallet.pgAccountNumberPlaceholder', required: true },
        { key: 'master', labelKey: 'wallet.pgRoutingNumber', placeholderKey: 'wallet.pgRoutingNumberPlaceholder', required: true }
      ]
    case 'chime':
      return [
        { key: 'account', labelKey: 'wallet.pgChimeSign', placeholderKey: 'wallet.pgChimeSignPlaceholder', required: true }
      ]
    case 'zelle':
      return [
        { key: 'account', labelKey: 'wallet.pgZelleSign', placeholderKey: 'wallet.pgZelleSignPlaceholder', required: true }
      ]
    default:
      return [
        { key: 'account', labelKey: 'wallet.pgReceiveInfo', placeholderKey: 'wallet.pgReceiveInfoPlaceholder', required: true }
      ]
  }
}
